import { useEffect, useState } from "react";
import Loading from "../Loading";
import { getRequestStatus } from "../../api/shorts-api";
import "./ProcessingScreen.css";

let interval;
const PROGRESS = {
  FAILED: {
    value: 0,
    message: "Failed",
    stringValue: "failed",
  },
  CANCELLED: {
    value: 0,
    message: "Cancelled",
    stringValue: "cancelled",
  },
  WAITING_IN_QUEUE: {
    value: 0,
    message: "Waiting in queue...",
    stringValue: "waiting_in_queue",
  },
  FETCHING_VIDEO: {
    value: 10,
    message: "Fetching video...",
    stringValue: "fetching_video",
  },
  TRANSCRIBING_AUDIO: {
    value: 20,
    message: "Analyzing video...",
    stringValue: "transcribing_audio",
  },
  OPEN_AI_SUGGESTIONS: {
    value: 40,
    message: "Finding interesting segments",
    stringValue: "open_ai_suggestions",
  },
  FINALIZING_DATA: {
    value: 90,
    message: "Finalizing Data...",
    stringValue: "finalizing_data",
  },
  TASK_COMPLETE: {
    value: 100,
    message: "Task complete!",
    stringValue: "task_complete",
  },
};

// Visual pipeline shown to the user during the (10-30 min) wait.
const STEPS = [
  { label: "Queued", value: 0 },
  { label: "Fetching", value: 10 },
  { label: "Analyzing", value: 20 },
  { label: "Highlights", value: 40 },
  { label: "Finalizing", value: 90 },
  { label: "Ready", value: 100 },
];

const SparkleIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path
      d="M12 3l1.9 4.6L18.5 9.5 13.9 11.4 12 16l-1.9-4.6L5.5 9.5l4.6-1.9L12 3z"
      fill="currentColor"
    />
    <path d="M19 14l.8 2 2 .8-2 .8-.8 2-.8-2-2-.8 2-.8.8-2z" fill="currentColor" opacity="0.85" />
  </svg>
);

const CheckIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path d="M5 12.5l4.2 4.2L19 7" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const AlertIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path d="M12 8v5" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" />
    <circle cx="12" cy="16.6" r="1.3" fill="currentColor" />
    <path d="M12 3l9.5 16.5H2.5L12 3z" stroke="currentColor" strokeWidth="2.2" strokeLinejoin="round" />
  </svg>
);

const InfoIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <circle cx="12" cy="12" r="9.2" stroke="currentColor" strokeWidth="1.8" />
    <path d="M12 11v5" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" />
    <circle cx="12" cy="8" r="1.15" fill="currentColor" />
  </svg>
);

const ProcessingScreen = ({ request, onSuccess, previousProgress, previousmessage }) => {
  const previousFailed = previousmessage === "failed" || previousmessage === "Failed";
  const [progress, setProgress] = useState(previousProgress || 0);
  const [message, setMessage] = useState(previousmessage || PROGRESS.WAITING_IN_QUEUE.message);
  const [requestId, setRequestId] = useState("");

  const [hasError, setHasError] = useState(previousFailed);
  const [failReason, setFailReason] = useState("");
  const [showSpinner, setShowSpinner] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    setRequestId(request._id);
  }, [request]);

  useEffect(() => {
    const fetchStatus = async () => {
      try {
        const fetchedRequest = await getRequestStatus(requestId);
        const stringValue = fetchedRequest.status;
        setFailReason(fetchedRequest?.failureReason || "");
        const progressItem = findProgressByStringValue(stringValue);
        if (progressItem) {
          setProgress(progressItem.value);
          setMessage(progressItem.message);
        }

        if (
          stringValue === PROGRESS.FAILED.stringValue ||
          stringValue === PROGRESS.TASK_COMPLETE.stringValue
        ) {
          clearInterval(interval);

          if (stringValue === PROGRESS.FAILED.stringValue) {
            setHasError(true);
            setShowSpinner(false);
          }

          if (stringValue === PROGRESS.TASK_COMPLETE.stringValue) {
            window.location.reload();
          }
        }
      } catch (error) {
        clearInterval(interval);
      }
    };

    interval = setInterval(fetchStatus, 10000);

    return () => clearInterval(interval);
  }, [requestId, onSuccess]);

  const findProgressByStringValue = (stringValue) => {
    for (let key in PROGRESS) {
      if (PROGRESS.hasOwnProperty(key)) {
        if (PROGRESS[key].stringValue === stringValue) {
          return PROGRESS[key];
        }
      }
    }
    return undefined;
  };

  const copyLink = () => {
    const link = window.location.href;
    const done = () => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    };
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(link).then(done).catch(() => {});
    } else {
      done();
    }
  };

  const activeIndex = STEPS.reduce((acc, s, i) => (progress >= s.value ? i : acc), 0);
  const stepState = (i) => {
    if (progress >= 100) return "is-done";
    if (i < activeIndex) return "is-done";
    if (i === activeIndex) return "is-active";
    return "";
  };

  if (hasError) {
    return (
      <div className="scproc-wrap">
        <div className="scproc-card is-error">
          <div className="scproc-badge is-error">
            <AlertIcon />
          </div>
          <h1 className="scproc-title">Something went wrong</h1>
          <p className="scproc-sub">
            We couldn't finish creating your clips. Please try again in a moment.
          </p>
          {showSpinner ? (
            <div className="scproc-spinner-wrap">
              <Loading />
            </div>
          ) : (
            failReason && (
              <div className="scproc-error-reason">
                <span className="scproc-error-label">Error reason</span>
                {failReason}
              </div>
            )
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="scproc-wrap">
      <div className="scproc-card">
        <div className="scproc-badge">
          <span className="scproc-badge-ring" />
          <SparkleIcon />
        </div>

        <h1 className="scproc-title">AI is analyzing your video</h1>
        <p className="scproc-sub">Sit back — this usually takes 10–30 minutes.</p>

        <div className="scproc-bar-head">
          <span className="scproc-status">
            <span className="scproc-dot" />
            {message}
          </span>
          <span className="scproc-pct">{progress}%</span>
        </div>

        <div className="scproc-track">
          <div className="scproc-fill" style={{ width: `${progress}%` }}>
            <span className="scproc-shimmer" />
          </div>
        </div>

        <ul className="scproc-steps">
          {STEPS.map((s, i) => (
            <li key={s.label} className={`scproc-step ${stepState(i)}`}>
              <span className="scproc-step-dot">
                <CheckIcon />
              </span>
              <span className="scproc-step-label">{s.label}</span>
            </li>
          ))}
        </ul>

        <div className="scproc-note">
          <InfoIcon />
          <div>
            <strong>Keep this page open</strong> while we create your clips. You can also{" "}
            <button
              type="button"
              className={`scproc-copy ${copied ? "is-copied" : ""}`}
              onClick={copyLink}
            >
              {copied ? "Link copied!" : "copy this page's link"}
            </button>{" "}
            and return later to check on your clips.
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProcessingScreen;
